import { useEffect, useState } from "react";
import { ResponsiveLine } from "@nivo/line";
import { getDailySalesTrend } from "../services/SaleService";
import {
  useTheme,
  Box,
  Select,
  MenuItem,
  FormControl,
  useMediaQuery,
  Fade,
} from "@mui/material";
import { tokens } from "../theme";

const SalesTrend = ({ isDashboard = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery("(max-width:380px)");
  const [rawData, setRawData] = useState([]);
  const [data, setData] = useState([]);
  const [chartYear, setChartYear] = useState("");
  const [filterType, setFilterType] = useState("month"); // default filter
  const TICK_COUNT = 10; // desired number of horizontal grid lines / ticks

  // Abbreviated formatter for Y-axis (₦1K, ₦1M, ₦1B)
  const formatAbbreviatedCurrency = (value) => {
    if (value == null) return "₦0";
    if (value >= 1_000_000_000)
      return `₦${(value / 1_000_000_000).toFixed(1)}B`;
    if (value >= 1_000_000) return `₦${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `₦${(value / 1_000).toFixed(1)}K`;
    return `₦${value}`;
  };

  // Full currency formatter with commas for tooltip
  const formatFullCurrency = (value) => {
    if (value == null) return "₦0";
    return `₦${Number(value).toLocaleString("en-NG")}`;
  };

  useEffect(() => {
    getDailySalesTrend()
      .then((res) => {
        if (!res.data || res.data.length === 0) return;
        setRawData(res.data);
      })
      .catch((error) => {
        console.error("Error fetching daily sales trend:", error);
      });
  }, []);

  useEffect(() => {
    if (rawData.length === 0) return;

    // 1. Sort data by date ascending
    const sortedRaw = [...rawData].sort(
      (a, b) => new Date(a.date) - new Date(b.date),
    );

    // 2. Identify the most recent date in the dataset
    const maxDate = new Date(sortedRaw[sortedRaw.length - 1].date);
    const currentYear = maxDate.getFullYear();

    // 3. Apply the date filter cutoff
    let filtered = sortedRaw;
    const cutoff = new Date(maxDate);

    if (filterType === "week") {
      cutoff.setDate(cutoff.getDate() - 7);
      filtered = sortedRaw.filter((d) => new Date(d.date) > cutoff);
    } else if (filterType === "2weeks") {
      cutoff.setDate(cutoff.getDate() - 14);
      filtered = sortedRaw.filter((d) => new Date(d.date) > cutoff);
    } else if (filterType === "month") {
      cutoff.setDate(cutoff.getDate() - 30);
      filtered = sortedRaw.filter((d) => new Date(d.date) > cutoff);
    } else if (filterType === "3months") {
      cutoff.setMonth(cutoff.getMonth() - 3);
      filtered = sortedRaw.filter((d) => new Date(d.date) > cutoff);
    } else if (filterType === "6months") {
      cutoff.setMonth(cutoff.getMonth() - 6);
      filtered = sortedRaw.filter((d) => new Date(d.date) > cutoff);
    } else if (filterType === "year") {
      cutoff.setFullYear(cutoff.getFullYear() - 1);
      filtered = sortedRaw.filter((d) => new Date(d.date) > cutoff);
    }

    // 4. Aggregate data to prevent overlapping/squishing
    const groupedMap = new Map();
    filtered.forEach((d) => {
      const dateObj = new Date(d.date);
      let label = "";

      // If viewing 6 months, a whole year or all-time, group by Month and Year (e.g., "Jan '24") to save space
      if (
        filterType === "6months" ||
        filterType === "year" ||
        filterType === "all"
      ) {
        const yr = dateObj.getFullYear().toString().slice(-2);
        const mth = dateObj.toLocaleDateString("en-GB", { month: "short" });
        label = `${mth} '${yr}`;
      } else {
        // If viewing week, 2 weeks, 1 month, or 3 months group by Day (e.g., "01 Jan")
        label = dateObj.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
        });
      }

      const currentVal = groupedMap.get(label) || 0;
      groupedMap.set(label, currentVal + (d.totalRevenue ?? 0));
    });

    let finalData = Array.from(groupedMap, ([x, y]) => ({ x, y }));

    // Fallback if no data matches the filter
    if (finalData.length === 0) {
      finalData = [{ x: "No Data", y: 0 }];
    }

    // 5. Calculate Dynamic Chart Year for the axis legend
    let displayYear = currentYear.toString();
    if (filtered.length > 0) {
      const minYear = new Date(filtered[0].date).getFullYear();
      const maxFilterDate = new Date(
        filtered[filtered.length - 1].date,
      ).getFullYear();
      if (minYear !== maxFilterDate) {
        displayYear = `${minYear} - ${maxFilterDate}`; // e.g. "2023 - 2024"
      } else {
        displayYear = maxFilterDate.toString();
      }
    }

    setData([{ id: "Total Sales", data: finalData }]);
    setChartYear(displayYear);
  }, [rawData, filterType]);

  const chartMargin = isDashboard
    ? {
        top: 40,
        right: 20,
        bottom: isMobile ? 80 : 60,
        left: isMobile ? 60 : 90,
      }
    : {
        top: 70,
        right: isMobile ? 20 : 130,
        bottom: isMobile ? 100 : 80,
        left: isMobile ? 60 : 110,
      };

  if (rawData.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          color: colors.gray[100],
          padding: "20px",
        }}
      >
        Loading chart...
      </div>
    );
  }

  // compute numeric tick values based on the actual Y values so grid lines align with ticks
  const computeTicks = (values, count) => {
    if (!values || values.length === 0) return [];
    const nums = values.map((d) => Number(d.y) || 0);
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    if (min === max) {
      const base = Math.floor(min || 0);
      return [base, base + 1, base + 2].slice(0, count);
    }
    const step = (max - min) / Math.max(1, count - 1);
    const ticks = [];
    for (let i = 0; i < count; i++) {
      const v = Math.round(min + step * i);
      ticks.push(v);
    }
    ticks[ticks.length - 1] = Math.round(max);
    return Array.from(new Set(ticks));
  };

  const rawPoints = data[0]?.data || [];
  const yTicks = computeTicks(rawPoints, TICK_COUNT);

  // Dynamically calculate x-axis tick values to prevent overlapping on mobile and dense desktop views
  const getXAxisTickValues = () => {
    if (rawPoints.length <= 5) return undefined; // Let Nivo handle it automatically if points are few

    let step = 1;
    if (isMobile) {
      // Tighter skipping on mobile screens
      step = Math.ceil(rawPoints.length / (isExtraSmall ? 3 : 5));
    } else {
      // Ensure we don't display more than ~12 labels to prevent clashing on desktop
      step = Math.ceil(rawPoints.length / 12);
    }

    if (step <= 1) return undefined;

    return rawPoints.filter((_, i) => i % step === 0).map((d) => d.x);
  };

  return (
    <Box sx={{ height: "100%", width: "100%", position: "relative" }}>
      {/* Dynamic Filter Dropdown */}
      <FormControl
        sx={{
          position: "absolute",
          top: isDashboard ? 0 : -20,
          right: isDashboard ? 20 : 0,
          zIndex: 10,
          minWidth: 100,
        }}
      >
        <Select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          size="small"
          sx={{
            fontSize: "12px",
            height: "32px",
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "#ffffff",
            color: colors.gray[100],
            boxShadow:
              theme.palette.mode === "dark"
                ? "none"
                : "0 1px 3px rgba(0,0,0,0.05)",
            transition: "all 0.2s ease",
            ".MuiOutlinedInput-notchedOutline": {
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.15)"
                  : "rgba(0,0,0,0.15)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.blueAccent[400],
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.blueAccent[500],
            },
            ".MuiSvgIcon-root ": { fill: colors.gray[100] },
          }}
          MenuProps={{
            TransitionComponent: Fade,
            transitionDuration: 300,
            PaperProps: {
              sx: {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
                    : "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(16px)",
                border: `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.15)"
                    : "rgba(0, 0, 0, 0.1)"
                }`,
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
                    : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
                borderRadius: "12px",
                mt: 1,
                "& .MuiMenuItem-root": {
                  fontSize: "13px",
                  fontWeight: 600,
                  padding: "8px 16px",
                  margin: "4px 8px",
                  borderRadius: "8px",
                  transition: "all 0.2s ease-in-out",
                  color: colors.gray[100],
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.04)",
                  },
                  "&:active": { transform: "scale(0.95)" },
                  "&.Mui-selected": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.15)"
                        : "rgba(0, 0, 0, 0.08)",
                  },
                },
              },
            },
          }}
        >
          <MenuItem value="week">1 Week</MenuItem>
          <MenuItem value="2weeks">2 Weeks</MenuItem>
          <MenuItem value="month">1 Month</MenuItem>
          <MenuItem value="3months">3 Months</MenuItem>
          <MenuItem value="6months">6 Months</MenuItem>
          <MenuItem value="year">1 Year</MenuItem>
          <MenuItem value="all">All Time</MenuItem>
        </Select>
      </FormControl>

      <ResponsiveLine
        data={data}
        enablePoints={false}
        enableGridY={true}
        enableGridX={false}
        enableArea={true}
        areaBaselineValue={yTicks[0] || 0} // Makes the area rest cleanly on the bottom grid line
        theme={{
          axis: {
            domain: { line: { stroke: "transparent" } },
            legend: { text: { fill: colors.gray[100] } },
            ticks: {
              line: { stroke: colors.gray[100], strokeWidth: 1 },
              text: { fill: colors.gray[100] },
            },
          },
          grid: {
            line: {
              stroke: colors.gray?.[400] ?? "rgba(255,255,255,0.12)",
              strokeWidth: 1,
              strokeDasharray: "4 4",
            },
          },
          legends: { text: { fill: colors.gray[100] } },
          tooltip: { container: { color: colors.primary[500] } },
          crosshair: {
            line: {
              stroke: colors.gray[400], // Light mode compatible crosshair
              strokeWidth: 1,
              strokeDasharray: "4 4",
            },
          },
        }}
        margin={chartMargin}
        xScale={{ type: "point" }}
        yScale={{
          type: "linear",
          min: "auto",
          max: "auto",
          stacked: true,
          reverse: false,
        }}
        axisBottom={{
          orient: "bottom",
          tickSize: 5,
          tickPadding: 5,
          tickRotation: isMobile ? -45 : -30,
          tickValues: getXAxisTickValues(),
          legend: isDashboard ? undefined : `Date (${chartYear})`,
          legendOffset: isMobile ? 80 : 60,
          legendPosition: "middle",
        }}
        axisLeft={{
          orient: "left",
          tickSize: 5,
          tickValues: yTicks,
          tickPadding: 5,
          legend: isDashboard ? undefined : "Total Sales",
          legendOffset: isMobile ? -50 : -100,
          legendPosition: "middle",
          format: formatAbbreviatedCurrency,
        }}
        curve="catmullRom"
        pointSize={10}
        pointColor={{ theme: "background" }}
        pointBorderWidth={2}
        pointBorderColor={{ from: "serieColor" }}
        pointLabelYOffset={-12}
        useMesh={true}
        gridYValues={yTicks}
        tooltip={({ point }) => (
          <div
            style={{
              padding: "8px 10px",
              background: theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              color: colors.gray[100],
              borderRadius: "6px",
              fontSize: "0.85rem",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0px 4px 12px rgba(0,0,0,0.4)"
                  : "0px 4px 12px rgba(0,0,0,0.15)",
              border: `1px solid ${theme.palette.mode === "dark" ? "transparent" : "rgba(0,0,0,0.1)"}`,
            }}
          >
            <div style={{ fontWeight: 500, marginBottom: "4px" }}>
              {point.data.xFormatted}
            </div>
            <div style={{ color: colors.blueAccent[500], fontWeight: 600 }}>
              {/* Uses the raw numeric y value to ensure proper comma placement */}
              {formatFullCurrency(point.data.y)}
            </div>
          </div>
        )}
        legends={
          isDashboard
            ? []
            : [
                {
                  anchor: "bottom-right",
                  direction: "column",
                  translateX: 100,
                  itemWidth: 80,
                  itemHeight: 20,
                  itemOpacity: 0.75,
                  symbolSize: 12,
                  symbolShape: "circle",
                  symbolBorderColor: "rgba(0, 0, 0, .5)",
                  effects: [{ on: "hover", style: { itemOpacity: 1 } }],
                },
              ]
        }
      />
    </Box>
  );
};

export default SalesTrend;
